import { useContext, useState } from 'react';
import { SelectGenre } from './Select/SelectGenre';
import { SelectLookingFor } from './Select/SelectLookingFor';
import { createOrUpdateCard } from '../utils/createOrUpdateCard';
import { deleteData } from '../utils/deleteData';
import { MdDelete, MdEdit, MdSave } from 'react-icons/md';
import { getLocalAvatar, resolveAvatarUrl } from '../utils/avatarUtils';
import { API_BASE_URL } from '../utils/apiBaseUrl';
import { GlobalContext } from '../context/GlobalState';
import { uploadImage } from '../utils/uploadImage';

export const MyBandCard = ({ band }) => {
  const { loadBands, showNotification } = useContext(GlobalContext);
  const {
    cardId,
    name,
    genre,
    description,
    looking_for,
    image_url,
    uploadedImageId,
  } = band;

  const [isEditing, setIsEditing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [imageError, setImageError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [editedData, setEditedData] = useState({
    name,
    genre,
    description,
    looking_for,
  });

  const handleEdit = () => {
    setPreviewImageUrl(resolveAvatarUrl(image_url, name, uploadedImageId));
    setImageError('');
    setSelectedImage(null);
    setIsEditing(true);
  };
  const handleDelete = async () => {
    try {
      await deleteData(`${API_BASE_URL}/bands/${cardId}`);
      await loadBands(true);
      showNotification('Band card deleted.', 'warning');
    } catch (error) {
      showNotification('Could not delete band card.', 'error');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    const { name, genre, description, looking_for } = editedData;
    let nextImageUrl = image_url;
    let nextUploadedImageId = uploadedImageId || null;

    try {
      if (selectedImage) {
        const uploadedImage = await uploadImage(selectedImage);
        nextImageUrl = uploadedImage.image_url;
        nextUploadedImageId = uploadedImage.uploadedImageId;
      }

      await createOrUpdateCard(
        `${API_BASE_URL}/bands/${cardId}`,
        {
          name,
          genre,
          description,
          looking_for,
          image_url: nextImageUrl,
          uploadedImageId: nextUploadedImageId,
        },
        'PATCH',
      );
      await loadBands(true);
      setIsEditing(false);
      setSelectedImage(null);
      setImageError('');
      showNotification('Band card updated.', 'success');
    } catch (error) {
      setImageError('Image upload failed. Please try again.');
      showNotification('Could not update band card.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setEditedData(prevData => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const handleImageChange = event => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedImage(null);
      setPreviewImageUrl(resolveAvatarUrl(image_url, name, uploadedImageId));
      return;
    }

    if (!file.type.startsWith('image/')) {
      setImageError('Please select a valid image file.');
      return;
    }

    setImageError('');
    setSelectedImage(file);
    setPreviewImageUrl(URL.createObjectURL(file));
  };

  return (
    <li className='card'>
      {!isEditing && (
        <img
          src={resolveAvatarUrl(image_url, name, uploadedImageId)}
          alt='band'
          loading='lazy'
          decoding='async'
          onError={e => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = getLocalAvatar(name);
          }}
        />
      )}
      {isEditing ? (
        <>
          <div className='form-item'>
            <label htmlFor={`band-photo-${cardId}`}>Card Photo</label>
            <div className='form-image-preview-wrap'>
              <img
                className='form-image-preview'
                src={
                  previewImageUrl ||
                  resolveAvatarUrl(image_url, name, uploadedImageId)
                }
                alt='Band card preview'
              />
            </div>
            <input
              id={`band-photo-${cardId}`}
              type='file'
              accept='image/*'
              onChange={handleImageChange}
            />
            {imageError && <span className='message-error'>{imageError}</span>}
          </div>
          <input
            type='text'
            placeholder='Band name'
            value={editedData.name}
            onChange={e => handleInputChange('name', e.target.value)}
          />
          <SelectGenre
            handleInputChange={handleInputChange}
            data={editedData}
          />

          <input
            type='text'
            placeholder='Tell something about your band'
            value={editedData.description}
            onChange={e => handleInputChange('description', e.target.value)}
          />
          <SelectLookingFor
            handleInputChange={handleInputChange}
            data={editedData}
          />
        </>
      ) : (
        <>
          <div className='card-title'>{name}</div>
          <div className='card-divider' />
          <div className='card-meta'>Genre: {genre}</div>
          <div className='card-description'>{description}</div>
          <div className='card-section'>
            <span className='card-section-label'>Looking For</span>
            {looking_for.map(element => (
              <span className='chip searching-for' key={element}>
                {element}
              </span>
            ))}
          </div>
        </>
      )}

      <div className='fav-apply-container'>
        {isEditing ? (
          <button className='action-button action-save' onClick={handleSave}>
            <MdSave />
            <span>{isSaving ? 'Saving...' : 'Save'}</span>
          </button>
        ) : (
          <div className='container-card-bottom'>
            <button className='action-button' title='edit' onClick={handleEdit}>
              <MdEdit />
              <span>Edit</span>
            </button>
            <button
              title='delete'
              className='action-button button-delete'
              onClick={handleDelete}
            >
              <MdDelete />
              <span>Delete</span>
            </button>
          </div>
        )}
      </div>
    </li>
  );
};
